import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
import { Funnel } from 'lucide-react';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import TaskForm from './components/TaskForm';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getTasks, deleteTask, updateTask, createTask, getAssignedLeads, getAuditTasks, getAuditAssignees, reassignAuditTask } from './api/taskApi';
import { useAuth } from '../../contexts/AuthProvider';
import api from '../../api';
import { filterExcludedAuditTasks } from '../../utils/auditTaskUtils';
import { buildAuditTaskFilterOptions, filterAuditTasks } from './auditTaskFilterUtils';

const TaskManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadPersistedState, persistState } = usePersistedFilters('task');
  /** Task Data tab only — server-side search via GET /tasks?search= */
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Helper: parse comma-separated URL param to array (for multi-select filters)
  const parseParamToArray = (val) => {
    if (!val || val === 'all') return [];
    const arr = String(val).split(',').map((s) => s.trim()).filter(Boolean);
    return arr.length > 0 ? arr : [];
  };

  // Normalize filter value to array (string or array from multi-select)
  const toFilterArray = (val) => {
    if (val == null) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (val === '' || val === 'all') return [];
    return [String(val).trim()];
  };

  // Parse URL search into filter state (all select filters as arrays for multi-select)
  const parseFilterDataFromSearch = (search) => {
    const params = new URLSearchParams(search || location.search);
    return {
      period: '',
      startDate: '',
      endDate: '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || '',
      dueDateFrom: params.get('dueDateFrom') || '',
      dueDateTo: params.get('dueDateTo') || '',
      status: parseParamToArray(params.get('status')),
      type: params.get('type') || '',
      priority: parseParamToArray(params.get('priority')),
      assignedUser: parseParamToArray(params.get('assignedUser')),
      department: parseParamToArray(params.get('department')),
      branch: parseParamToArray(params.get('branch')),
      sector: parseParamToArray(params.get('sector')),
      source: parseParamToArray(params.get('source')),
      standard: parseParamToArray(params.get('standard'))
    };
  };

  const getEmptyTaskFilters = () => ({
    period: '',
    startDate: '',
    endDate: '',
    dateFrom: '',
    dateTo: '',
    dueDateFrom: '',
    dueDateTo: '',
    status: [],
    type: '',
    priority: [],
    assignedUser: [],
    department: [],
    branch: [],
    sector: [],
    source: [],
    standard: []
  });

  const initialFilterData = parseFilterDataFromSearch(location.search);
  const [filterData, setFilterData] = useState(initialFilterData);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [auditTasks, setAuditTasks] = useState([]);
  const [isLoadingAuditTasks, setIsLoadingAuditTasks] = useState(false);
  const [auditAssignees, setAuditAssignees] = useState([]);
  const [isLoadingAuditAssignees, setIsLoadingAuditAssignees] = useState(false);
  const [reassigningAuditTaskId, setReassigningAuditTaskId] = useState(null);
  const [reassignSearch, setReassignSearch] = useState('');

  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTargetTask, setReassignTargetTask] = useState(null);
  const [reassignSelectedUser, setReassignSelectedUser] = useState(null);
  const [reassignNotes, setReassignNotes] = useState('');
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowReassignModal(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const [auditTaskQuery, setAuditTaskQuery] = useState('');
  const [auditTaskQuickFilter, setAuditTaskQuickFilter] = useState('all');
  const [auditTaskProcessStage, setAuditTaskProcessStage] = useState('all');
  const [auditTaskCreatedSort, setAuditTaskCreatedSort] = useState('newest'); // 'newest' | 'oldest'
  const emptyAuditTaskFilters = {
    quickFilter: 'all', processStage: 'all', assignedUser: '', client: '',
    standard: '', service: '', auditCycle: '', taskType: '',
  };
  const [auditTaskFilters, setAuditTaskFilters] = useState(emptyAuditTaskFilters);
  const [auditTaskFilterDraft, setAuditTaskFilterDraft] = useState(emptyAuditTaskFilters);
  const [auditTaskPage, setAuditTaskPage] = useState(1);
  const AUDIT_TASK_PAGE_SIZE = 10;

  const handleAuditTaskFilterChange = (key, value) => {
    setAuditTaskFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleAuditTaskFilterApply = () => {
    setAuditTaskQuickFilter(auditTaskFilterDraft.quickFilter || 'all');
    setAuditTaskProcessStage(auditTaskFilterDraft.processStage || 'all');
    setAuditTaskFilters(auditTaskFilterDraft);
  };

  const handleAuditTaskFilterReset = () => {
    setAuditTaskFilterDraft(emptyAuditTaskFilters);
    setAuditTaskFilters(emptyAuditTaskFilters);
    setAuditTaskQuickFilter('all');
    setAuditTaskProcessStage('all');
  };

  const activeAuditTaskFilterCount = Object.entries(auditTaskFilters).filter(([key, value]) => (
    value && !(['quickFilter', 'processStage'].includes(key) && value === 'all')
  )).length;
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [activeTab, setActiveTab] = useState('task-data');
  const [assignedLeadsFilterData, setAssignedLeadsFilterData] = useState({
    createdBy: '',
    assignedTo: '',
    stage: '',
    dateFrom: '',
    dateTo: ''
  });

  // Task list pagination (backend returns Spring Page: content, totalElements, number 0-based)
  const [taskPage, setTaskPage] = useState(1);
  const [taskPageSize, setTaskPageSize] = useState(10);
  const [taskTotalRows, setTaskTotalRows] = useState(0);
  /** Server-side column sort (Spring `sort=field,direction`); key null = default API order */
  const [taskSort, setTaskSort] = useState({ key: null, direction: 'asc' });

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading, hasRole } = useAuth();
  const isAdmin = hasRole('ROLE_ADMIN');
  const canAuditProcess =
    isAdmin ||
    hasRole('ROLE_DIRECTOR') ||
    can('view', 'audit') ||
    can('view_own', 'audit') ||
    can('view_department', 'audit') ||
    can('view_global', 'audit');

  const parseRequestedTab = (search) => {
    const requestedTab = new URLSearchParams(search || location.search).get('tab');

    if (requestedTab === 'assigned-leads') {
      return 'assigned-leads';
    }

    if (requestedTab === 'audit-task' && canAuditProcess) {
      return 'audit-task';
    }

    return 'task-data';
  };

  const handleTabChange = (nextTab) => (event) => {
    event.preventDefault();

    if (nextTab === activeTab) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    if (nextTab === 'task-data') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', nextTab);
    }

    navigate({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : ''
    });
  };
  
  // Helper function to check task-specific capabilities
  const canTask = (capabilityKey) => can(capabilityKey, 'task');
  
  // ✅ TASK UPDATE PERMISSION: Check if user can view task update date
  const canViewTaskUpdate = can('canviewtaskupdate', 'task');

  // Profile uses departmentId; some payloads use departementId (matches backend spelling)
  const userDepartmentId = user?.departmentId ?? user?.departementId;

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const resolveAssigneeName = (task) => {
    if (!task) return '-';
    const name = task?.assignedUsername ?? task?.assignedUser ?? task?.assignedUserName;
    if (name) return name;
    const id = task?.assignedUserId;
    if (id != null) return `User #${id}`;
    return 'Unassigned';
  };

  // State for filter options
  const [filterOptions, setFilterOptions] = useState({
    assignedUsers: [],
    departments: [],
    sectors: [],
    sources: [],
    standards: [],
    statuses: [
      { value: 'to do', label: 'To Do' },
      { value: 'in progress', label: 'In Progress' },
      { value: 'done', label: 'Done' }
    ],
    priorities: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' }
    ],
    assignedLeadsUsers: [],
    assignedLeadsStages: []
  });

  // Update filters when URL parameters change (e.g. from Dashboard "Companies Contacted" chart)
  useEffect(() => {
    const urlFilters = parseFilterDataFromSearch(location.search);
    setFilterData(urlFilters);
    setAppliedFilters(urlFilters);
  }, [location.search]);

  // Sync direct links like /task?tab=audit-task with the visible tab state.
  useEffect(() => {
    if (!capabilitiesLoading) {
      const requestedTab = parseRequestedTab(location.search);
      if (requestedTab !== activeTab) {
        setActiveTab(requestedTab);
      }
    }
  }, [location.search, capabilitiesLoading, canAuditProcess, activeTab]);

  const isAuditTaskMode = activeTab === 'audit-task';

  useEffect(() => {
    const linkedAssignee = new URLSearchParams(location.search).get('auditAssignee');
    if (linkedAssignee && activeTab === 'audit-task') setAuditTaskQuery(linkedAssignee);
  }, [location.search, activeTab]);

  useEffect(() => {
    if (!capabilitiesLoading) {
      const initializeData = async () => {
        try {
          const hasUrlParams = location.search.length > 1;
          if (!hasUrlParams) {
            const saved = loadPersistedState();
            if (saved) {
              const restoredFilters = saved.filters ?? getEmptyTaskFilters();
              const restoredSearch = saved.search ?? '';
              const restoredPage = saved.page ?? 0;
              const restoredSize = saved.size ?? 10;

              setFilterData(restoredFilters);
              setAppliedFilters(restoredFilters);
              setTaskSearchQuery(restoredSearch);
              setTaskPage(restoredPage + 1);
              setTaskPageSize(restoredSize);

              await Promise.allSettled([
                loadTasks(restoredFilters, { page: restoredPage + 1, size: restoredSize }),
                fetchFilterOptions()
              ]);
              return;
            }
          }
          await Promise.allSettled([
            loadTasks(),
            fetchFilterOptions()
          ]);
        } catch (error) {
          console.error('Error during initialization:', error);
        }
      };

      initializeData();
    }
  }, [capabilitiesLoading]);

  // ✅ Reload tasks when URL parameters change (e.g., from dashboard navigation)
  // Use parsed URL filters so we load with correct params (avoids stale filterData)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip on initial mount
    }
    if (!capabilitiesLoading) {
      const urlFilters = parseFilterDataFromSearch(location.search);
      setTaskPage(1);
      loadTasks(urlFilters, { page: 1 });
    }
  }, [location.search, capabilitiesLoading]);

  const prevTaskSearchRef = useRef(null);
  useEffect(() => {
    if (capabilitiesLoading) return;
    if (prevTaskSearchRef.current === null) {
      prevTaskSearchRef.current = taskSearchQuery;
      return;
    }
    if (prevTaskSearchRef.current === taskSearchQuery) return;
    prevTaskSearchRef.current = taskSearchQuery;
    const id = setTimeout(() => {
      setTaskPage(1);
      persistState({ filters: appliedFilters, search: taskSearchQuery, page: 0, size: taskPageSize });
      loadTasks(null, { page: 1 });
    }, 350);
    return () => clearTimeout(id);
  }, [taskSearchQuery, capabilitiesLoading]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };


  // Helper function to convert to sentence case
  const toSentenceCase = (str) => {
    if (!str) return '';
    const lower = str.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return 'badge badge-outline text-gray';
    const lowerPriority = priority.toLowerCase();
    const colors = {
      'high': 'bg-danger text-white',
      'medium': 'bg-warning text-white',
      'low': 'bg-info text-white'
    };
    return `badge ${colors[lowerPriority] || 'badge-outline text-gray'}`;
  };

  const getStatusBadge = (status) => {
    if (!status) return 'badge badge-outline text-gray';
    const lowerStatus = status.toLowerCase();
    const colors = {
      'done': 'badge badge-outline text-success',
      'to do': 'badge badge-outline text-primary',
      'in progress': 'badge badge-outline text-warning'
    };
    return `badge ${colors[lowerStatus] || 'badge-outline text-gray'}`;
  };

  // Helper: format date as dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return dateString;
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return dateString;
    }
  };

  const parseValidDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  };

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const getWeekBounds = (baseDate) => {
    const d = startOfDay(baseDate);
    // Monday as start of week
    const day = d.getDay(); // 0 (Sun) .. 6 (Sat)
    const diffToMonday = (day + 6) % 7;
    const weekStart = new Date(d);
    weekStart.setDate(weekStart.getDate() - diffToMonday);
    const weekEnd = endOfDay(new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000));
    return { weekStart, weekEnd };
  };

  const getAuditTaskCategory = (task, now = new Date()) => {
    const status = (task?.status || '').toLowerCase();
    const isDone = status === 'done' || status === 'completed' || status === 'closed' || status === 'cancelled' || status === 'canceled';
    if (isDone) return 'completed';

    const due = parseValidDate(task?.dueAt ?? task?.dueDate);
    if (!due) return 'no-due';

    const todayStart = startOfDay(now);
    const dueStart = startOfDay(due);
    if (dueStart < todayStart) return 'overdue';
    if (dueStart.getTime() === todayStart.getTime()) return 'due-today';

    const { weekStart, weekEnd } = getWeekBounds(now);
    if (dueStart >= weekStart && dueStart <= weekEnd) return 'this-week';
    return 'upcoming';
  };

  const AUDIT_PROCESS_STAGES = [
    { key: 'application-review', label: 'Application Review' },
    { key: 'scheduling', label: 'Scheduling' },
    { key: 'audit', label: 'Audit' },
    { key: 'technical-review', label: 'Technical Review' },
    { key: 'certificate', label: 'Certificate' },
  ];

  const getAuditProcessStage = (task) => {
    const code = String(task?.taskTypeCode || '').toUpperCase();
    if (code.includes('CERTIFICATE')) return 'certificate';
    if (code.includes('TECHNICAL_REVIEW')) return 'technical-review';
    if (code.includes('SCHEDUL')) return 'scheduling';
    if (code.includes('APPLICATION_REVIEW') || code.includes('REVIEWER')) return 'application-review';
    if (code.includes('AUDIT')) return 'audit';
    return 'other';
  };

  const formatDueLabel = (dueDateValue) => {
    const due = parseValidDate(dueDateValue);
    if (!due) return '-';

    const now = new Date();
    const today = startOfDay(now);
    const dueDay = startOfDay(due);
    const diffDays = Math.round((dueDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

    let dayLabel = '';
    if (diffDays === -1) dayLabel = 'Yesterday';
    else if (diffDays === 0) dayLabel = 'Today';
    else if (diffDays === 1) dayLabel = 'Tomorrow';
    else {
      const { weekStart, weekEnd } = getWeekBounds(now);
      if (dueDay >= weekStart && dueDay <= weekEnd) {
        dayLabel = due.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
      } else {
        dayLabel = due.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }

    // Only show time if the input seems to include time info
    const raw = String(dueDateValue);
    const hasTime = raw.includes('T') || raw.match(/\d{2}:\d{2}/);
    if (hasTime) {
      const hh = String(due.getHours()).padStart(2, '0');
      const mm = String(due.getMinutes()).padStart(2, '0');
      return `${dayLabel}, ${hh}.${mm}`;
    }
    return dayLabel;
  };

  const auditTaskStats = useMemo(() => {
    const now = new Date();
    const stats = {
      overdue: 0,
      dueToday: 0,
      thisWeek: 0,
      completed: 0,
      active: 0
    };

    for (const task of auditTasks || []) {
      const category = getAuditTaskCategory(task, now);
      if (category === 'completed') {
        stats.completed += 1;
        continue;
      }

      // active = not done (regardless due date)
      stats.active += 1;
      if (category === 'overdue') stats.overdue += 1;
      else if (category === 'due-today') stats.dueToday += 1;
      else if (category === 'this-week') stats.thisWeek += 1;
    }

    return stats;
  }, [auditTasks]);

  const auditTaskOptionSets = useMemo(() => buildAuditTaskFilterOptions(auditTasks), [auditTasks]);

  const auditTaskFilterFields = useMemo(() => ([
    {
      key: 'quickFilter',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Active' },
        { value: 'completed', label: `Completed (${auditTaskStats.completed})` },
      ],
    },
    {
      key: 'processStage',
      label: 'Audit Process Stage',
      type: 'select',
      options: [
        { value: 'all', label: 'All Stages' },
        ...AUDIT_PROCESS_STAGES.map((stage) => ({ value: stage.key, label: stage.label })),
      ],
    },
    { key: 'assignedUser', label: 'User Assigned', type: 'select', options: auditTaskOptionSets.assignees },
    { key: 'client', label: 'Client', type: 'select', options: auditTaskOptionSets.clients },
    { key: 'standard', label: 'Standard', type: 'select', options: auditTaskOptionSets.standards },
    { key: 'service', label: 'Program Category', type: 'select', options: auditTaskOptionSets.services },
    { key: 'auditCycle', label: 'Audit Cycle', type: 'select', options: auditTaskOptionSets.cycles },
    { key: 'taskType', label: 'Task Type', type: 'select', options: auditTaskOptionSets.taskTypes },
  ]), [auditTaskStats.completed, auditTaskOptionSets]);

  const filteredAuditAssignees = useMemo(() => {
    const list = auditAssignees || [];
    const q = (reassignSearch || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => (u?.fullName || '-').toLowerCase().includes(q));
  }, [auditAssignees, reassignSearch]);

  const filteredAuditTasks = useMemo(() => {
    const now = new Date();
    const q = auditTaskQuery.trim().toLowerCase();

    let rows = (auditTasks || []).filter((t) => {
      const category = getAuditTaskCategory(t, now);
      if (auditTaskQuickFilter === 'completed') return category === 'completed';
      return category !== 'completed'; // active tasks only when not viewing completed
    });

    if (auditTaskQuickFilter !== 'all' && auditTaskQuickFilter !== 'completed') {
      rows = rows.filter((t) => getAuditTaskCategory(t, now) === auditTaskQuickFilter);
    }

    if (auditTaskProcessStage !== 'all') {
      rows = rows.filter((t) => getAuditProcessStage(t) === auditTaskProcessStage);
    }

    rows = filterAuditTasks(rows, auditTaskFilters);

    if (q) {
      rows = rows.filter((t) => {
        const haystack = [
          t?.taskTitle,
          t?.title,
          t?.taskType,
          t?.taskTypeCode,
          t?.description,
          t?.companyName,
          t?.projectCode,
          t?.contactName,
          t?.type,
          t?.assignedUser,
          t?.assignedUsername,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    if (auditTaskCreatedSort) {
      const dir = auditTaskCreatedSort === 'oldest' ? 1 : -1;
      rows.sort((a, b) => {
        const da = parseValidDate(a?.createdAt ?? a?.created_at);
        const db = parseValidDate(b?.createdAt ?? b?.created_at);
        if (da && db) return (da.getTime() - db.getTime()) * dir;
        if (da && !db) return -1;
        if (!da && db) return 1;
        return 0;
      });
      return rows;
    }

    // Sort: overdue first, then due today, then this week, then upcoming; within each by dueDate asc
    const rank = {
      overdue: 0,
      'due-today': 1,
      'this-week': 2,
      upcoming: 3,
      'no-due': 4
    };

    rows.sort((a, b) => {
      const ca = getAuditTaskCategory(a, now);
      const cb = getAuditTaskCategory(b, now);
      const ra = rank[ca] ?? 99;
      const rb = rank[cb] ?? 99;
      if (ra !== rb) return ra - rb;

      const da = parseValidDate(a?.dueAt ?? a?.dueDate);
      const db = parseValidDate(b?.dueAt ?? b?.dueDate);
      if (da && db) return da.getTime() - db.getTime();
      if (da && !db) return -1;
      if (!da && db) return 1;
      return (String(a?.taskTitle || a?.title || '')).localeCompare(String(b?.taskTitle || b?.title || ''));
    });

    return rows;
  }, [auditTasks, auditTaskQuery, auditTaskQuickFilter, auditTaskProcessStage, auditTaskCreatedSort, auditTaskFilters]);

  const auditTaskTotalPages = Math.max(1, Math.ceil(filteredAuditTasks.length / AUDIT_TASK_PAGE_SIZE));

  useEffect(() => {
    setAuditTaskPage(1);
  }, [auditTaskQuery, auditTaskQuickFilter, auditTaskProcessStage, auditTaskCreatedSort, auditTaskFilters, auditTasks]);

  useEffect(() => {
    if (auditTaskPage > auditTaskTotalPages) {
      setAuditTaskPage(auditTaskTotalPages);
    }
  }, [auditTaskPage, auditTaskTotalPages]);

  const paginatedAuditTasks = useMemo(() => {
    const start = (auditTaskPage - 1) * AUDIT_TASK_PAGE_SIZE;
    return filteredAuditTasks.slice(start, start + AUDIT_TASK_PAGE_SIZE);
  }, [filteredAuditTasks, auditTaskPage]);

  const loadAuditTasks = async () => {
    setIsLoadingAuditTasks(true);
    try {
      const data = await getAuditTasks();
      setAuditTasks(filterExcludedAuditTasks(data));
    } catch (error) {
      console.error('Error loading audit tasks:', error);
      showAlert('danger', 'Failed to load audit process');
      setAuditTasks([]);
    } finally {
      setIsLoadingAuditTasks(false);
    }
  };

  useEffect(() => {
    if (!capabilitiesLoading && activeTab === 'audit-task') {
      loadAuditTasks();
    }
  }, [activeTab, capabilitiesLoading]);

  const loadAuditAssignees = async (taskTypeCode) => {
    setIsLoadingAuditAssignees(true);
    try {
      const data = await getAuditAssignees(taskTypeCode);
      setAuditAssignees(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error loading audit assignees:', e);
      setAuditAssignees([]);
    } finally {
      setIsLoadingAuditAssignees(false);
    }
  };

  // Assignees are loaded per-task when modal opens, not globally on tab load

  const handleAuditReassign = async (taskId, assignedUserId, notes) => {
    if (!taskId || !assignedUserId) return;
    setReassigningAuditTaskId(taskId);
    try {
      await reassignAuditTask(taskId, assignedUserId, notes);

      // Update local list immediately
      const selected = (auditAssignees || []).find((u) => String(u?.id) === String(assignedUserId));
      setAuditTasks((prev) => (prev || []).map((t) => {
        if (String(t?.id) !== String(taskId)) return t;
        return {
          ...t,
          assignedUserId,
          assignedUsername: selected?.fullName || t?.assignedUsername
        };
      }));
      showAlert('success', 'Audit task reassigned');
    } catch (e) {
      console.error('Reassign audit task failed:', e);
      showAlert('danger', 'Failed to reassign audit process');
    } finally {
      setReassigningAuditTaskId(null);
    }
  };

  // Fetch sectors data from backend
  const fetchSectorsData = async () => {
    try {
      const response = await api.get('/dashboard/sectors');
      if (response.data && response.data.sectors) {
        setFilterOptions(prev => ({
          ...prev,
          sectors: response.data.sectors.map(s => ({
            value: String(s.id),
            label: s.name
          }))
        }));
      }
    } catch (error) {
      console.error('Error fetching sectors data:', error);
      setFilterOptions(prev => ({
        ...prev,
        sectors: []
      }));
    }
  };

  // Fetch sources data from backend
  const fetchSourcesData = async () => {
    try {
      const response = await api.get('/dashboard/sources');
      if (response.data && response.data.sources) {
        setFilterOptions(prev => ({
          ...prev,
          sources: response.data.sources.map(s => ({
            value: String(s.id),
            label: s.name
          }))
        }));
      }
    } catch (error) {
      console.error('Error fetching sources data:', error);
      setFilterOptions(prev => ({
        ...prev,
        sources: []
      }));
    }
  };

  // Fetch standards data from backend
  const fetchStandardsData = async () => {
    try {
      const response = await api.get('/dashboard/standards');
      if (response.data && response.data.standards) {
        setFilterOptions(prev => ({
          ...prev,
          standards: response.data.standards.map(s => ({
            value: String(s.id),
            label: s.name
          }))
        }));
      }
    } catch (error) {
      console.error('Error fetching standards data:', error);
      setFilterOptions(prev => ({
        ...prev,
        standards: []
      }));
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      // Fetch assigned users - filter based on permissions
      let assignedUsers = [];
      try {
        const usersRes = await api.get('/md/internal-users/option-internal-user');
        let usersData = usersRes.data || [];
        
        // ✅ Permission-based filtering for assigned users:
        // 1. view_own → only show current user
        // 2. view_department → only show users in same department
        // 3. view_global → show all users
        if (!canTask('view_global')) {
          if (canTask('view_own')) {
            // Only show current user
            if (user?.id) {
              usersData = usersData.filter(u => u.id === user.id);
            }
          } else if (canTask('view_department')) {
            if (userDepartmentId != null) {
              const uid = String(userDepartmentId);
              usersData = usersData.filter((u) => String(u.departmentId ?? u.departementId ?? '') === uid);
            }
          }
        }
        
        // Values must be user IDs — backend GET /tasks uses assignedUserId with parseIdFilter (digits only)
        assignedUsers = usersData.map(u => ({
          value: String(u.id),
          label: u.fullName || `${u.firstName} ${u.lastName}`.trim(),
          departmentId: u.departmentId || u.departementId,
          status: u.status
        }));
      } catch (err) {
        console.error('Error fetching assigned users:', err);
        assignedUsers = [];
      }


      // Fetch departments from HRIS so IDs align with user departmentId for cascade
      let departments = [];
      try {
        const deptRes = await api.get('/md/departement');
        let deptData = deptRes.data || [];
        if (!canTask('view_global')) {
          if (userDepartmentId != null) {
            const userDeptId = String(userDepartmentId);
            deptData = deptData.filter((dept) => String(dept.id || '') === userDeptId);
          } else {
            deptData = [];
          }
        }
        departments = deptData.map((dept) => ({
          value: String(dept.id || ''),
          label: dept.name
        }));
      } catch (err) {
        console.error('Error fetching departments:', err);
        departments = [];
      }

      setFilterOptions(prev => ({
        ...prev,
        assignedUsers,
        departments
      }));
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Don't show alert for filter options error as it's not critical
      setFilterOptions(prev => ({
        ...prev,
        assignedUsers: [],
        departments: []
      }));
    }
  };

  // Fetch assigned leads filter options (users and stages)
  const fetchAssignedLeadsFilterOptions = async () => {
    try {
      // Fetch users for assigned leads filters (can reuse assigned users)
      let assignedLeadsUsers = [];
      try {
        const usersRes = await api.get('/md/internal-users/option-internal-user');
        let usersData = usersRes.data || [];
        
        // Apply same permission filtering as regular assigned users
        if (!canTask('view_global')) {
          if (canTask('view_own')) {
            if (user?.id) {
              usersData = usersData.filter(u => u.id === user.id);
            }
          } else if (canTask('view_department')) {
            if (userDepartmentId != null) {
              const uid = String(userDepartmentId);
              usersData = usersData.filter((u) => String(u.departmentId ?? u.departementId ?? '') === uid);
            }
          }
        }
        
        assignedLeadsUsers = usersData.map(u => ({
          value: u.fullName || `${u.firstName} ${u.lastName}`.trim(),
          label: u.fullName || `${u.firstName} ${u.lastName}`.trim()
        }));
      } catch (err) {
        console.error('Error fetching assigned leads users:', err);
        assignedLeadsUsers = [];
      }

      // Fetch stages for assigned leads filters
      let assignedLeadsStages = [];
      try {
        const stagesRes = await api.get('/crm/lead-statuses/options');
        assignedLeadsStages = (stagesRes.data || []).map(stage => ({
          value: stage.value || stage.id,
          label: stage.label || stage.name
        }));
      } catch (err) {
        console.error('Error fetching assigned leads stages:', err);
        assignedLeadsStages = [];
      }

      setFilterOptions(prev => ({
        ...prev,
        assignedLeadsUsers,
        assignedLeadsStages
      }));
    } catch (error) {
      console.error('Error fetching assigned leads filter options:', error);
      setFilterOptions(prev => ({
        ...prev,
        assignedLeadsUsers: [],
        assignedLeadsStages: []
      }));
    }
  };

  // Load sectors, sources and standards data on mount
  useEffect(() => {
    const loadSectorSourceStandardData = async () => {
      await Promise.allSettled([
        fetchSectorsData(),
        fetchSourcesData(),
        fetchStandardsData()
      ]);
      // ✅ Reload tasks after sectors/sources/standards are loaded if URL params exist
      // This ensures filters are applied correctly when navigating from dashboard
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('sector') || searchParams.get('source') || searchParams.get('standard')) {
        loadTasks();
      }
    };
    loadSectorSourceStandardData();
  }, []);

  const loadTasks = async (overrideFilter, options = {}) => {
    const effective = overrideFilter != null ? overrideFilter : appliedFilters;
    setIsLoading(true);
    try {
      // Build query params for permission-based filtering
      const params = {};

      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (created by OR assigned to)

      if (!canTask('view_global')) {
        if (canTask('view_department')) {
          if (userDepartmentId != null) {
            params.viewDepartmentId = userDepartmentId;
          }
        } else if (canTask('view_own')) {
          if (user?.id) {
            params.viewOwnUserId = user.id;
          }
        }
      }

      const departmentList = toFilterArray(effective.department).filter((value) => value !== 'all');
      if (departmentList.length > 0) params.departmentId = departmentList;
      // Period preset overrides manual dateFrom/dateTo
      const effectiveDateFrom = effective.startDate || effective.dateFrom;
      const effectiveDateTo = effective.endDate || effective.dateTo;
      if (effectiveDateFrom) params.dateFrom = effectiveDateFrom;
      if (effectiveDateTo) params.dateTo = effectiveDateTo;
      if (effective.dueDateFrom) params.dueDateFrom = effective.dueDateFrom;
      if (effective.dueDateTo) params.dueDateTo = effective.dueDateTo;
      if (effective.status != null && (Array.isArray(effective.status) ? effective.status.length > 0 : effective.status)) {
        params.status = effective.status;
      }
      const assignedVal = toFilterArray(effective.assignedUser);
      if (assignedVal.length > 0) params.assignedUser = assignedVal;
      const branchList = toFilterArray(effective.branch).filter((value) => value !== 'all');
      if (branchList.length > 0) params.branchId = branchList;

      const taskSearch = (taskSearchQuery || '').trim();
      if (taskSearch) params.search = taskSearch;

      // Pagination: backend uses 0-based page; options.page is 1-based when provided
      const pageOneBased = options.page != null ? options.page : taskPage;
      const size = options.size != null ? options.size : taskPageSize;
      params.page = pageOneBased - 1;
      params.size = size;

      const activeSort = options.sortConfig !== undefined ? options.sortConfig : taskSort;
      if (activeSort?.key) {
        params.sort = `${activeSort.key},${activeSort.direction}`;
      }

      const response = await getTasks(params);

      // Backend returns Spring Page: { content, totalElements, totalPages, number, size }
      const isPaginated = response && Array.isArray(response.content) && (response.totalElements != null);
      let data = isPaginated ? response.content : (Array.isArray(response) ? response : response.data || []);

      if (isPaginated) {
        setTaskTotalRows(response.totalElements);
      } else {
        setTaskTotalRows(data.length);
      }

      const statusList = Array.isArray(effective.status)
        ? (effective.status.length ? effective.status.map((s) => String(s).trim().toLowerCase()) : null)
        : (effective.status ? effective.status.split(',').map((s) => s.trim().toLowerCase()) : null);
      if (statusList && statusList.length > 0) {
        data = data.filter((task) => {
          const taskStatus = task.status?.toLowerCase();
          return taskStatus && statusList.includes(taskStatus);
        });
      }

      const priorityList = toFilterArray(effective.priority);
      if (priorityList.length > 0) {
        const prioritySet = new Set(priorityList.map((p) => p.toLowerCase()));
        data = data.filter((task) => task.priority && prioritySet.has(task.priority.toLowerCase()));
      }

      if (effective.type) {
        const filterType = effective.type.trim();
        data = data.filter((task) => {
          const taskType = task.type?.trim();
          const normalizedTaskType = taskType?.toLowerCase().replace(/[_-]/g, ' ');
          const normalizedFilterType = filterType.toLowerCase().replace(/[_-]/g, ' ');
          return normalizedTaskType === normalizedFilterType;
        });
      }

      const assignedList = toFilterArray(effective.assignedUser);
      if (assignedList.length > 0) {
        const assignedIdSet = new Set(assignedList.map((id) => parseInt(id, 10)).filter((n) => !isNaN(n)));
        data = data.filter((task) => task.assignedUserId != null && assignedIdSet.has(task.assignedUserId));
      }

      const sectorList = toFilterArray(effective.sector).filter((s) => s !== 'all');
      if (sectorList.length > 0) {
        const sectorSet = new Set(sectorList.map((s) => String(s)));
        data = data.filter((task) => task.sectorId != null && sectorSet.has(String(task.sectorId)));
      }

      const sourceList = toFilterArray(effective.source).filter((s) => s !== 'all');
      if (sourceList.length > 0) {
        const sourceSet = new Set(sourceList.map((s) => String(s)));
        data = data.filter((task) => task.sourceId != null && sourceSet.has(String(task.sourceId)));
      }

      const standardList = toFilterArray(effective.standard).filter((s) => s !== 'all');
      if (standardList.length > 0) {
        const standardSet = new Set(standardList.map((s) => String(s)));
        data = data.filter((task) => {
          const standardIds = task.standardIds;
          if (!standardIds || standardIds.length === 0) return false;
          return standardIds.some((sid) => standardSet.has(String(sid)));
        });
      }

      let formattedData = data.map((task) => ({
        ...task,
        createdAtFormatted: formatDateToDDMMYYYY(task.createdAt),
        updatedAtFormatted: formatDateToDDMMYYYY(task.updatedAt),
        createdAtOriginal: task.createdAt
      }));

      const clientDateFrom = effective.startDate || effective.dateFrom;
      const clientDateTo = effective.endDate || effective.dateTo;
      if (clientDateFrom) {
        const fromDate = new Date(clientDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        formattedData = formattedData.filter((task) => {
          if (!task.createdAtOriginal) return false;
          const taskDate = new Date(task.createdAtOriginal);
          taskDate.setHours(0, 0, 0, 0);
          return taskDate >= fromDate;
        });
      }

      if (clientDateTo) {
        const toDate = new Date(clientDateTo);
        toDate.setHours(23, 59, 59, 999);
        formattedData = formattedData.filter((task) => {
          if (!task.createdAtOriginal) return false;
          const taskDate = new Date(task.createdAtOriginal);
          return taskDate <= toDate;
        });
      }
      
      // Map back to display format
      // ✅ Backend already provides assignedUser and taskUpdateBy as "FirstName LastName"
      formattedData = formattedData.map(task => ({
        ...task,
        createdAt: task.createdAtFormatted,
        updatedAt: task.updatedAtFormatted
      }));
      
      setTasks(formattedData);

      // Sync pagination state when explicit options were used (e.g. reset to page 1)
      if (options.page != null) setTaskPage(options.page);
      if (options.size != null) setTaskPageSize(options.size);
    } catch (error) {
      console.error('Error loading tasks:', error);
      showAlert('danger', 'Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    const emptyFilters = getEmptyTaskFilters();
    setFilterData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setTaskSort({ key: null, direction: 'asc' });
    setTaskPage(1);
    persistState({ filters: emptyFilters, search: taskSearchQuery, page: 0, size: taskPageSize });
    loadTasks(emptyFilters, { page: 1, sortConfig: { key: null, direction: 'asc' } });
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setTaskPage(1);
    persistState({ filters: applied, search: taskSearchQuery, page: 0, size: taskPageSize });
    loadTasks(applied, { page: 1 });
  };

  const resolveTaskOptionLabels = (options = [], selected = []) => {
    const selectedValues = Array.isArray(selected) ? selected : toFilterArray(selected);
    const labels = selectedValues.map((value) => {
      const match = (options || []).find((opt) => String(opt.value ?? opt.id) === String(value));
      return match?.label ?? match?.name ?? String(value);
    }).filter(Boolean);
    return labels;
  };

  // Build userId → { deptId, status } lookup — same pattern as Leads/Clients
  const taskUserDeptLookup = useMemo(() => {
    const map = new Map();
    (filterOptions.assignedUsers || []).forEach(u => {
      if (u.value != null) map.set(String(u.value), { deptId: u.departmentId != null ? String(u.departmentId) : null, status: u.status });
    });
    return map;
  }, [filterOptions.assignedUsers]);

  // Cascade: filter Assigned User by selected department(s), mark inactive
  const cascadedTaskAssignedUsers = useMemo(() => {
    const selectedDepts = toFilterArray(filterData.department).filter(d => d !== 'all');
    const base = (filterOptions.assignedUsers || []).map(u => {
      const entry = taskUserDeptLookup.get(String(u.value));
      return entry?.status === '0' ? { ...u, label: `${u.label} (Inactive)` } : u;
    });
    if (!selectedDepts.length) return base;
    return base.filter(u => {
      const entry = taskUserDeptLookup.get(String(u.value));
      return entry?.deptId != null && selectedDepts.includes(entry.deptId);
    });
  // eslint-disable-next-line
  }, [filterData.department, filterOptions.assignedUsers, taskUserDeptLookup]);

  const taskFilterFields = useMemo(() => [
    { key: 'dueDate', label: 'Due Date', type: 'dateRange', fromKey: 'dueDateFrom', toKey: 'dueDateTo' },
    { key: 'department', label: 'Department', type: 'multiselect', options: filterOptions.departments },
    { key: 'assignedUser', label: 'Assigned To', type: 'multiselect', options: cascadedTaskAssignedUsers },
    { key: 'status', label: 'Status', type: 'multiselect', options: filterOptions.statuses },
    { key: 'priority', label: 'Priority', type: 'multiselect', options: filterOptions.priorities },
    { key: 'sector', label: 'Sector', type: 'multiselect', options: filterOptions.sectors },
    { key: 'source', label: 'Source', type: 'multiselect', options: filterOptions.sources },
    { key: 'standard', label: 'Standard', type: 'multiselect', options: filterOptions.standards },
  // eslint-disable-next-line
  ], [filterOptions, cascadedTaskAssignedUsers]);

  const activeTaskFilterCount = getActiveFilterCount(appliedFilters, taskFilterFields);

  const taskFilterTags = useMemo(() => {
    const tags = [];
    if (appliedFilters.period) {
      tags.push({ key: 'period', label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}` });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({ key: 'period', label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}` });
    } else if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      tags.push({ key: 'dateRange', label: `Created Date: ${appliedFilters.dateFrom || '...'} → ${appliedFilters.dateTo || '...'}` });
    }
    if (appliedFilters.dueDateFrom || appliedFilters.dueDateTo) tags.push({ key: 'dueDateRange', label: `Due Date: ${appliedFilters.dueDateFrom || '...'} → ${appliedFilters.dueDateTo || '...'}` });
    if (appliedFilters.type) tags.push({ key: 'type', label: `Type: ${appliedFilters.type}` });
    resolveTaskOptionLabels(filterOptions.statuses, appliedFilters.status).forEach((label) => tags.push({ key: `status:${label}`, label: `Status: ${label}` }));
    resolveTaskOptionLabels(filterOptions.priorities, appliedFilters.priority).forEach((label) => tags.push({ key: `priority:${label}`, label: `Priority: ${label}` }));
    resolveTaskOptionLabels(filterOptions.assignedUsers, appliedFilters.assignedUser).forEach((label) => tags.push({ key: `assignedUser:${label}`, label: `Assigned: ${label}` }));
    resolveTaskOptionLabels(filterOptions.departments, appliedFilters.department).forEach((label) => tags.push({ key: `department:${label}`, label: `Department: ${label}` }));
    resolveTaskOptionLabels(filterOptions.sectors, appliedFilters.sector).forEach((label) => tags.push({ key: `sector:${label}`, label: `Sector: ${label}` }));
    resolveTaskOptionLabels(filterOptions.sources, appliedFilters.source).forEach((label) => tags.push({ key: `source:${label}`, label: `Source: ${label}` }));
    resolveTaskOptionLabels(filterOptions.standards, appliedFilters.standard).forEach((label) => tags.push({ key: `standard:${label}`, label: `Standard: ${label}` }));
    if ((taskSearchQuery || '').trim()) tags.push({ key: 'search', label: `Search: "${taskSearchQuery.trim()}"` });
    return tags;
  }, [appliedFilters, filterOptions, taskSearchQuery]);

  const handleTaskChipRemove = (key) => {
    const nextFilters = { ...appliedFilters };
    if (key === 'period') {
      nextFilters.period = '';
      nextFilters.startDate = '';
      nextFilters.endDate = '';
    } else if (key === 'dateRange') {
      nextFilters.dateFrom = '';
      nextFilters.dateTo = '';
    } else if (key === 'dueDateRange') {
      nextFilters.dueDateFrom = '';
      nextFilters.dueDateTo = '';
    } else if (key === 'type') {
      nextFilters.type = '';
    } else if (key === 'search') {
      setTaskSearchQuery('');
    } else if (key.startsWith('status:')) {
      const label = key.slice('status:'.length);
      nextFilters.status = resolveTaskOptionLabels(filterOptions.statuses, appliedFilters.status)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.statuses || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('priority:')) {
      const label = key.slice('priority:'.length);
      nextFilters.priority = resolveTaskOptionLabels(filterOptions.priorities, appliedFilters.priority)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.priorities || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('assignedUser:')) {
      const label = key.slice('assignedUser:'.length);
      nextFilters.assignedUser = resolveTaskOptionLabels(filterOptions.assignedUsers, appliedFilters.assignedUser)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.assignedUsers || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('department:')) {
      const label = key.slice('department:'.length);
      nextFilters.department = resolveTaskOptionLabels(filterOptions.departments, appliedFilters.department)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.departments || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('sector:')) {
      const label = key.slice('sector:'.length);
      nextFilters.sector = resolveTaskOptionLabels(filterOptions.sectors, appliedFilters.sector)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.sectors || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('source:')) {
      const label = key.slice('source:'.length);
      nextFilters.source = resolveTaskOptionLabels(filterOptions.sources, appliedFilters.source)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.sources || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    } else if (key.startsWith('standard:')) {
      const label = key.slice('standard:'.length);
      nextFilters.standard = resolveTaskOptionLabels(filterOptions.standards, appliedFilters.standard)
        .filter((item) => item !== label)
        .map((item) => {
          const match = (filterOptions.standards || []).find((opt) => (opt.label ?? opt.name) === item);
          return match?.value ?? match?.id ?? item;
        });
    }

    setFilterData(prev => ({ ...prev, ...nextFilters }));
    setAppliedFilters(nextFilters);
    setTaskPage(1);
    persistState({ filters: nextFilters, search: taskSearchQuery, page: 0, size: taskPageSize });
    loadTasks(nextFilters, { page: 1 });
  };

  const handleTaskPageChange = (newPage) => {
    setTaskPage(newPage);
    persistState({ filters: appliedFilters, search: taskSearchQuery, page: newPage - 1, size: taskPageSize });
    loadTasks(null, { page: newPage });
  };

  const handleTaskRowsPerPageChange = (size) => {
    setTaskPageSize(size);
    setTaskPage(1);
    persistState({ filters: appliedFilters, search: taskSearchQuery, page: 0, size });
    loadTasks(null, { page: 1, size });
  };

  const handleTaskSortChange = (cfg) => {
    setTaskSort(cfg);
    setTaskPage(1);
    loadTasks(null, { page: 1, sortConfig: cfg });
  };

  // Assigned leads filter handlers
  const handleAssignedLeadsFilterChange = (name, value) => {
    setAssignedLeadsFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignedLeadsFilterReset = () => {
    setAssignedLeadsFilterData({
      createdBy: '',
      assignedTo: '',
      stage: '',
      dateFrom: '',
      dateTo: ''
    });
    showAlert('info', 'Assigned leads filter reset');
  };

  const handleAssignedLeadsFilterApply = () => {
    // No need to reload data, filtering is done client-side
    showAlert('success', 'Assigned leads filter applied');
  };

  // ✅ TASK UPDATE PERMISSION: Memoize columns to recalculate when permission changes
  const columns = useMemo(() => [
    {
      key: 'clientName',
      label: 'Client',
      className: 'text-truncate',
      render: (value, row) => (
        row.clientId ? (
          <a
            href={`/clients/${row.clientId}`}
            className="text-primary text-truncate d-inline-block text-decoration-none"
            style={{
              cursor: 'pointer',
              maxWidth: '200px',
              width: '200px'
            }}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey || e.button === 1) {
                return;
              }
              e.preventDefault();
              navigate(`/clients/${row.clientId}`);
            }}
            title={value}
          >
            {value}
          </a>
        ) : (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '200px', width: '200px' }}
            title={value}
          >
            {value}
          </span>
        )
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      className: 'text-truncate',
      render: (value) => {
        if (!value) return <span className="text-muted">-</span>;
        const tags = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'projectName',
      label: 'Project Name',
      className: 'text-truncate',
      render: (value, row) => (
        row.leadId ? (
          <a
            href={`/leads/detail/${row.leadId}`}
            className="text-primary text-truncate d-inline-block text-decoration-none"
            style={{
              cursor: 'pointer',
              maxWidth: '250px',
              width: '250px'
            }}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey || e.button === 1) {
                return;
              }
              e.preventDefault();
              navigate(`/leads/detail/${row.leadId}`);
            }}
            title={value}
          >
            {value}
          </a>
        ) : (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '250px', width: '250px' }}
            title={value}
          >
            {value}
          </span>
        )
      )
    },
    { 
      key: 'assignedUser', 
      label: 'Assigned To',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '160px', width: '160px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'contactName', 
      label: 'Contact Name',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '180px', width: '180px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'type', 
      label: 'Type Task',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '120px', width: '120px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'taskTitle', 
      label: 'Task Title',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'description', 
      label: 'Description',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '250px', width: '250px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'file', 
      label: 'File',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'priority', 
      label: 'Priority',
      className: 'text-truncate',
      render: (value) => {
        const displayValue = toSentenceCase(value);
        return (
          <span
            className={`${getPriorityBadge(value)} text-truncate d-inline-block`}
            style={{ maxWidth: '100px', width: '100px' }}
            title={displayValue}
          >
            {displayValue}
          </span>
        );
      }
    },
    { 
      key: 'status', 
      label: 'Status',
      className: 'text-truncate',
      render: (value) => {
        const displayValue = toSentenceCase(value);
        return (
          <span
            className={`${getStatusBadge(value)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={displayValue}
          >
            {displayValue}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'date',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '120px', width: '120px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      type: 'date',
      className: 'text-truncate',
      render: (value, row) => {
        if (!value || value === '') {
          return (
            <span
              className="text-truncate d-inline-block text-muted"
              style={{ maxWidth: '120px', width: '120px' }}
              title="No due date set"
            >
              -
            </span>
          );
        }

        // Check task status - if done, show in green
        const isDone = row.status?.toLowerCase() === 'done';

        // Check if due date is past (overdue) - but only if not done
        const isOverdue = !isDone && (() => {
          if (!value) return false;
          try {
            const dueDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < today;
          } catch (e) {
            return false;
          }
        })();

        // Determine styling based on status
        let className = 'text-truncate d-inline-block';
        let title = value;

        if (isDone) {
          className += ' text-success fw-bold';
          title = `Completed: ${value}`;
        } else if (isOverdue) {
          className += ' text-danger fw-bold';
          title = `Overdue: ${value}`;
        }

        return (
          <span
            className={className}
            style={{ maxWidth: '120px', width: '120px' }}
            title={title}
          >
            {value}
          </span>
        );
      }
    },
    // ✅ TASK UPDATE PERMISSION: Only show Updated At column if user has permission
    ...(canViewTaskUpdate ? [
      {
        key: 'updatedAt', 
        label: 'Updated At',
        type: 'date',
        className: 'text-truncate',
        render: (value) => (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '120px', width: '120px' }}
            title={value}
          >
            {value}
          </span>
        )
      },
      {
        key: 'taskUpdateBy', 
        label: 'Task Update By',
        className: 'text-truncate',
        render: (value) => {
          // ✅ Backend provides taskUpdateBy as "FirstName LastName"
          const displayValue = value || '-';
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '160px', width: '160px' }}
              title={displayValue}
            >
              {displayValue}
            </span>
          );
        }
      }
    ] : [])
  ], [canViewTaskUpdate, capabilitiesLoading]);

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedTask) {
        if (!canTask('edit')) {
          showAlert('danger', 'You do not have permission to edit tasks.');
          return;
        }
        await updateTask(selectedTask.id, formData);
        showAlert('success', 'Task updated successfully');
      } else {
        if (!canTask('create')) {
          showAlert('danger', 'You do not have permission to create tasks.');
          return;
        }
        await createTask(formData);
        showAlert('success', 'Task created successfully');
      }
      await loadTasks();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving task:', error);
      showAlert('danger', 'Failed to save task');
    }
  };

  const handleEdit = (row) => {
    if (!canTask('edit')) {
      showAlert('danger', 'You do not have permission to edit tasks.');
      return;
    }
    setSelectedTask(row);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    if (!canTask('delete')) {
      showAlert('danger', 'You do not have permission to delete tasks.');
      return;
    }
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    if (!canTask('create')) {
      showAlert('danger', 'You do not have permission to create tasks.');
      return;
    }
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTask(itemToDelete.id);
      await loadTasks();
      showAlert('success', 'Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      showAlert('danger', 'Failed to delete task');
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleView = (row) => {
    // Redirect to leads detail page if leadId exists, otherwise go to company/clients page
    if (row.leadId) {
      navigate(`/leads/detail/${row.leadId}`);
    } else if (row.clientId) {
      navigate(`/clients/${row.clientId}`);
    } else {
      showAlert('warning', 'No associated lead or company information found');
    }
  };

  const handleExportExcel = () => {
    if (!canTask('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    showAlert('info', 'Export to Excel functionality coming soon');
  };

  const handleExportPDF = () => {
    if (!canTask('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    showAlert('info', 'Export to PDF functionality coming soon');
  };

  // Load assigned leads (created by someone else, assigned to current user)
  const loadAssignedLeads = async () => {
    if (!user?.id) return;
    
    setIsLoadingLeads(true);
    try {
      // Use the new dedicated API endpoint for assigned leads
      const leads = await getAssignedLeads();
      
      // Format dates
      const formattedLeads = leads.map(lead => ({
        ...lead,
        createdAtFormatted: formatDateToDDMMYYYY(lead.createdAt),
        updatedAtFormatted: formatDateToDDMMYYYY(lead.updatedAt)
      }));
      
      setAssignedLeads(formattedLeads);
    } catch (error) {
      console.error('Error loading assigned leads:', error);
      showAlert('danger', 'Failed to load assigned leads');
    } finally {
      setIsLoadingLeads(false);
    }
  };

  // Load assigned leads when component mounts and user is available
  useEffect(() => {
    if (!capabilitiesLoading && user?.id && activeTab === 'assigned-leads') {
      Promise.allSettled([
        loadAssignedLeads(),
        fetchAssignedLeadsFilterOptions()
      ]);
    }
  }, [capabilitiesLoading, user?.id, activeTab]);

  // Filter assigned leads based on search query
  const filteredAssignedLeads = useMemo(() => {
    if (!searchQuery.trim()) {
      return assignedLeads;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return assignedLeads.filter(lead => {
      const projectName = (lead.projectName || '').toLowerCase();
      const companyName = (lead.companyName || '').toLowerCase();
      const stage = (lead.stage || '').toLowerCase();
      const pic = (lead.pic || '').toLowerCase();
      const email = (lead.email || '').toLowerCase();
      const createdBy = lead.createdBy 
        ? `${lead.createdBy.firstName || ''} ${lead.createdBy.lastName || ''}`.toLowerCase().trim()
        : (lead.createdByFirstName && lead.createdByLastName 
          ? `${lead.createdByFirstName} ${lead.createdByLastName}`.toLowerCase().trim()
          : '');
      const assignedTo = lead.assignedTo 
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.toLowerCase().trim()
        : (lead.assignedToFirstName && lead.assignedToLastName 
          ? `${lead.assignedToFirstName} ${lead.assignedToLastName}`.toLowerCase().trim()
          : '');
      
      return projectName.includes(query) ||
             companyName.includes(query) ||
             stage.includes(query) ||
             pic.includes(query) ||
             email.includes(query) ||
             createdBy.includes(query) ||
             assignedTo.includes(query);
    });
  }, [assignedLeads, searchQuery]);

  // Columns for assigned leads table
  const assignedLeadsColumns = useMemo(() => [
    {
      key: 'projectName',
      label: 'Project Name',
      className: 'text-truncate',
      render: (value, item) => (
        <a
          href={`/leads/detail/${item.id}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ 
            cursor: 'pointer',
            maxWidth: '200px',
            width: '200px'
          }}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return;
            }
            e.preventDefault();
            navigate(`/leads/detail/${item.id}`);
          }}
          title={value}
        >
          {value || '-'}
        </a>
      )
    },
    {
      key: 'companyName',
      label: 'Company Name',
      className: 'text-truncate',
      render: (value, item) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      className: 'text-truncate',
      render: (value) => {
        if (!value) return <span className="text-muted">-</span>;
        const tags = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    { 
      key: 'stage', 
      label: 'Stage',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'pic', 
      label: 'PIC',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'email', 
      label: 'Email',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'createdBy', 
      label: 'Created By',
      className: 'text-truncate',
      render: (value, item) => {
        const createdBy = item.createdBy;
        const displayValue = createdBy 
          ? `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim() 
          : (item.createdByFirstName && item.createdByLastName 
            ? `${item.createdByFirstName} ${item.createdByLastName}`.trim()
            : '-');
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '180px', width: '180px' }}
            title={displayValue}
          >
            {displayValue}
          </span>
        );
      }
    },
    { 
      key: 'assignedTo', 
      label: 'Assigned To',
      className: 'text-truncate',
      render: (value, item) => {
        const assignedTo = item.assignedTo;
        const displayValue = assignedTo 
          ? `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`.trim() 
          : (item.assignedToFirstName && item.assignedToLastName 
            ? `${item.assignedToFirstName} ${item.assignedToLastName}`.trim()
            : '-');
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '180px', width: '180px' }}
            title={displayValue}
          >
            {displayValue}
          </span>
        );
      }
    },
    {
      key: 'createdAtFormatted',
      label: 'Created At',
      type: 'date',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '120px', width: '120px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    }
  ], []);

  return (
    <>
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">{isAuditTaskMode ? 'Task Audit' : 'Task Management'}</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            {!isAuditTaskMode && canTask('create') && (
              <button 
                className="btn btn-primary"
                onClick={handleAddNew}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                New Tasks
              </button>
            )}
          </div>
        </div>
      </div>

      <TaskForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTask(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedTask}
        hasViewOwnPermission={canTask('view_own')}
        hasViewGlobalPermission={canTask('view_global')}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Task"
        message={`Are you sure you want to delete this task? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              {!isAuditTaskMode && (
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a 
                      href="#tabs-task-data" 
                      className={`nav-link ${activeTab === 'task-data' ? 'active' : ''}`} 
                      data-bs-toggle="tab" 
                      aria-selected={activeTab === 'task-data'}
                      role="tab"
                      onClick={handleTabChange('task-data')}
                    >
                      Task Data
                    </a>
                  </li>
                  <li className="nav-item-tab" role="presentation">
                    <a 
                      href="#tabs-assigned-leads" 
                      className={`nav-link ${activeTab === 'assigned-leads' ? 'active' : ''}`} 
                      data-bs-toggle="tab" 
                      aria-selected={activeTab === 'assigned-leads'}
                      role="tab"
                      onClick={handleTabChange('assigned-leads')}
                    >
                      Assigned Leads
                    </a>
                  </li>
                </ul>
              </div>
              )}
              <div className="card-body">
                <div className="tab-content">
                  <div className={`tab-pane ${activeTab === 'task-data' ? 'active show' : ''}`} id="tabs-task-data" role="tabpanel">
                    <GenericView
                      data={tasks}
                      columns={columns}
                      onEdit={canTask('edit') ? handleEdit : undefined}
                      onDelete={canTask('delete') ? handleDelete : undefined}
                      onView={handleView}
                      isLoading={isLoading || capabilitiesLoading}
                      controlledSortConfig={taskSort}
                      onSortChange={handleTaskSortChange}
                      pagination={{
                        currentPage: taskPage,
                        totalRows: taskTotalRows,
                        rowsPerPage: taskPageSize,
                        onPageChange: handleTaskPageChange,
                        onRowsPerPageChange: handleTaskRowsPerPageChange
                      }}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasTask" activeCount={activeTaskFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={taskSearchQuery}
                          onChange={setTaskSearchQuery}
                          placeholder="Search tasks..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canTask('export_data') ? handleExportExcel : undefined}
                          onExportPDF={canTask('export_data') ? handleExportPDF : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Task is showing a filtered view"
                          summary={`Filtered by ${activeTaskFilterCount} ${activeTaskFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={taskFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={isLoading || capabilitiesLoading}
                          activeCount={activeTaskFilterCount}
                          filterTags={taskFilterTags}
                          onRemoveFilter={handleTaskChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                    />
                  </div>
                  <div className={`tab-pane ${activeTab === 'assigned-leads' ? 'active show' : ''}`} id="tabs-assigned-leads" role="tabpanel">
                    <GenericView
                      data={filteredAssignedLeads}
                      columns={assignedLeadsColumns}
                      onView={(row) => {
                        if (row.id) {
                          navigate(`/leads/detail/${row.id}`);
                        }
                      }}
                      isLoading={isLoadingLeads || capabilitiesLoading}
                      filterComponent={
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasAssignedLeads"
                        >
                          <Funnel size={16} className="me-1" />
                          Filter
                        </button>
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search assigned leads..."
                        />
                      }
                    />
                  </div>
                  {canAuditProcess && (
                  <div className={`tab-pane ${activeTab === 'audit-task' ? 'active show' : ''}`} id="tabs-audit-task" role="tabpanel">
                    <div
                      className="mb-3 p-3 d-flex align-items-center gap-2 flex-wrap"
                      style={{
                        background: '#fff',
                        border: '1px solid #e6e9ef',
                        borderRadius: '14px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                      }}
                    >
                      <div className="position-relative flex-grow-1" style={{ minWidth: '240px' }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={18}
                          height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#8a94a6"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                        >
                          <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                          <path d="M21 21l-6 -6" />
                        </svg>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search tasks by title, company, project, or assignee..."
                          value={auditTaskQuery}
                          onChange={(e) => setAuditTaskQuery(e.target.value)}
                          style={{
                            paddingLeft: '40px',
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            borderRadius: '10px',
                            border: '1px solid #e6e9ef',
                            background: '#f8f9fb',
                          }}
                        />
                      </div>

                      <FilterButton
                        offcanvasId="offcanvasAuditTask"
                        activeCount={activeAuditTaskFilterCount}
                        onClick={() => setAuditTaskFilterDraft(auditTaskFilters)}
                      />

                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Toggle sort order"
                        onClick={() =>
                          setAuditTaskCreatedSort((prev) => (prev === 'newest' ? 'oldest' : 'newest'))
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={16}
                          height={16}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="me-1"
                          aria-hidden="true"
                        >
                          <path d="M3 8h4" />
                          <path d="M3 12h6" />
                          <path d="M3 16h6" />
                          <path d="M15 4v14" />
                          <path d="M18 15l-3 3l-3 -3" />
                        </svg>
                        {auditTaskCreatedSort === 'newest' ? 'Newest' : 'Oldest'}
                      </button>
                    </div>

                    {(isLoadingAuditTasks || capabilitiesLoading) ? (
                      <div className="text-center p-4 text-muted">Loading...</div>
                    ) : (
                      <div>
                        {filteredAuditTasks.length === 0 ? (
                          <div className="text-center p-5">
                            <div className="mb-2" style={{ fontSize: '2rem' }}>
                              {auditTaskQuickFilter === 'completed' ? '✅' : '📋'}
                            </div>
                            <div className="text-muted">
                              {auditTaskQuickFilter === 'completed' ? 'No completed tasks yet' : 'No tasks found'}
                            </div>
                          </div>
                        ) : (
                          paginatedAuditTasks.map((task) => {
                            const category = getAuditTaskCategory(task);
                            const isCompleted = category === 'completed';
                            const isReassigningThis = String(reassigningAuditTaskId) === String(task?.id);

                            const title = task?.title || task?.taskTitle || task?.taskType || '-';
                            const description = task?.description || task?.wording || '';
                            const company = task?.companyName || '';
                            const projectCode = task?.projectCode || '';
                            const assigneeName = resolveAssigneeName(task);
                            const taskTypeCode = String(task?.taskTypeCode || '').toUpperCase();
                            const taskTypeLabel = task?.taskType || task?.taskTitle || task?.title || 'Audit Process';

                            // Determine current workflow step from task type code
                            const workflowSteps = [
                              { id: 'initial-certification', label: 'Initial Certification', codes: ['INITIAL_CERTIFICATION'], color: '#8b5cf6' },
                              { id: 'initial-audit-stage-1', label: 'Initial Audit (Stage 1)', codes: ['INITIAL_AUDIT_STAGE_1'], color: '#f59e0b' },
                              { id: 'initial-audit-stage-2', label: 'Initial Audit (Stage 2)', codes: ['INITIAL_AUDIT_STAGE_2'], color: '#f59e0b' },
                              { id: 'review-audit-scheduling', label: 'Review & Audit Scheduling', codes: ['APPLICATION_REVIEW', 'AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER'], color: '#0d9488' },
                            ];
                            
                            const currentStep = workflowSteps.find((step) =>
                              step.codes.some((code) => taskTypeCode.includes(code))
                            ) || workflowSteps.find((step) => step.id === 'review-audit-scheduling'); // Default to Review & Audit Scheduling

                            // Build summary from project/task data so each card reflects its own project context.
                            const serviceName = String(task?.serviceName || '').trim();
                            const serviceItem = String(task?.serviceItem || '').trim();
                            // serviceName/serviceItem describe the project-wide active cycle and can
                            // remain on Initial after a Surveillance task is created. auditCycle is
                            // resolved from this task's own scope and is therefore authoritative.
                            const auditCycle = String(task?.auditCycle || '').trim();
                            const standardName = String(task?.standardName || '').trim();
                            const auditMethodology = String(task?.auditMethodology || '').trim();
                            const certificationLabel = auditCycle || serviceName || 'Initial Certification';
                            const stageKey = String(task?.stageKey || '').trim().toLowerCase();
                            const isInitialAuditStage1 = stageKey === 'stage1';
                            const isInitialAuditStage2 = stageKey === 'stage2';
                            const initialAuditFallbackLabel = isInitialAuditStage1
                              ? 'Initial Audit (Stage 1)'
                              : isInitialAuditStage2
                                ? 'Initial Audit (Stage 2)'
                                : 'Initial Audit (Stage 1 & Stage 2)';
                            const taskCycleLabel = auditCycle || serviceItem;
                            const serviceItemIsCombinedStageLabel = /stage\s*1\s*&\s*stage\s*2/i.test(taskCycleLabel);
                            const initialAuditLabel = (isInitialAuditStage1 || isInitialAuditStage2)
                              ? ((taskCycleLabel && !serviceItemIsCombinedStageLabel) ? taskCycleLabel : initialAuditFallbackLabel)
                              : (taskCycleLabel || initialAuditFallbackLabel);
                            const activeTaskLabel = taskTypeLabel || 'Review & Audit Scheduling';
                            const stepColor = currentStep.color;

                            // Task type accent color - use step color
                            const getColorFromHex = (hex) => {
                              const hexToRgb = (h) => {
                                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
                                return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
                              };
                              return {
                                color: hex,
                                light: `rgba(${hexToRgb(hex)}, 0.08)`,
                                bg: `rgba(${hexToRgb(hex)}, 0.12)`,
                                text: hex
                              };
                            };

                            const taskTypeAccent = getColorFromHex(stepColor);

                            // Status badge and styling
                            const { urgencyBadge, dueLine, cardBorderColor } = (() => {
                              if (isCompleted) return {
                                urgencyBadge: <span className="badge" style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}>✓ Completed</span>,
                                dueLine: task?.completedAt ? `Completed ${formatDateToDDMMYYYY(task.completedAt)}` : 'Completed',
                                cardBorderColor: '#bbf7d0',
                              };
                              if (category === 'overdue') return {
                                urgencyBadge: <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}>⚠ Overdue</span>,
                                dueLine: formatDueLabel(task?.dueAt ?? task?.created_at),
                                cardBorderColor: '#fca5a5',
                              };
                              if (category === 'due-today') return {
                                urgencyBadge: <span className="badge" style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>⏰ Due Today</span>,
                                dueLine: formatDueLabel(task?.dueAt ?? task?.created_at),
                                cardBorderColor: '#fde68a',
                              };
                              if (category === 'this-week') return {
                                urgencyBadge: <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>📅 This Week</span>,
                                dueLine: formatDueLabel(task?.dueAt ?? task?.created_at),
                                cardBorderColor: '#bfdbfe',
                              };
                              return {
                                urgencyBadge: null,
                                dueLine: formatDueLabel(task?.dueAt ?? task?.created_at),
                                cardBorderColor: '#e2e8f0',
                              };
                            })();

                            return (
                              <div
                                key={task.id ?? `${title}-${task.dueAt ?? task.dueDate ?? ''}`}
                                className="card mb-3"
                                style={{
                                  border: `1px solid ${cardBorderColor}`,
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                  opacity: isCompleted ? 0.85 : 1,
                                  transition: 'box-shadow 0.2s',
                                }}
                              >
                                {/* Top accent bar */}
                                <div style={{ height: '4px', background: isCompleted ? '#86efac' : taskTypeAccent.color }} />

                                {/* Card header: service + due date section */}
                                <div
                                  className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-between gap-2 flex-wrap"
                                  style={{ background: isCompleted ? 'rgba(220,252,231,0.4)' : taskTypeAccent.light }}
                                >
                                  {/* Workflow summary */}
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {projectCode && (
                                      <span
                                        className="badge fw-semibold"
                                        style={{
                                          background: 'rgba(13,148,136,0.16)',
                                          color: '#0f766e',
                                          border: '1px solid rgba(13,148,136,0.35)',
                                          fontSize: '0.72rem',
                                          padding: '0.35em 0.75em',
                                        }}
                                      >
                                        {projectCode}
                                      </span>
                                    )}
                                    <span className="fw-semibold" style={{ color: '#2b8a78', fontSize: '1.05rem' }}>
                                      {certificationLabel}
                                    </span>
                                    {standardName && (
                                      <>
                                        <span className="text-muted fw-semibold">|</span>
                                        <span
                                          className="badge fw-semibold"
                                          style={{
                                            background: 'rgba(139,92,246,0.16)',
                                            color: '#6d28d9',
                                            border: '1px solid rgba(139,92,246,0.35)',
                                            fontSize: '0.78rem',
                                            padding: '0.35em 0.75em',
                                          }}
                                        >
                                          {standardName}
                                        </span>
                                      </>
                                    )}
                                    <span className="text-muted fw-semibold">|</span>
                                    <span
                                      className="badge fw-semibold"
                                      style={{
                                        background: 'rgba(59,130,246,0.16)',
                                        color: '#1d4ed8',
                                        border: '1px solid rgba(59,130,246,0.35)',
                                        fontSize: '0.78rem',
                                        padding: '0.35em 0.75em',
                                      }}
                                    >
                                      {initialAuditLabel}
                                    </span>
                                    <span className="text-muted fw-semibold">|</span>
                                    <span
                                      className="badge fw-semibold"
                                      style={{
                                        background: 'rgba(250,204,21,0.2)',
                                        color: '#b45309',
                                        border: '1px solid rgba(245,158,11,0.35)',
                                        fontSize: '0.78rem',
                                        padding: '0.35em 0.75em',
                                      }}
                                    >
                                      {activeTaskLabel}
                                    </span>
                                    {(() => {
                                      const reviewDecision = String(task?.reviewDecision || '').toUpperCase();
                                      if (!reviewDecision || reviewDecision === 'DRAFT') return null;
                                      const reviewDecisionMeta = {
                                        APPROVED: { label: 'Approved', color: 'success' },
                                        APPROVED_WITH_NOTES: { label: 'Approved with Notes', color: 'orange' },
                                        REJECTED: { label: 'Rejected', color: 'danger' },
                                      }[reviewDecision];
                                      if (!reviewDecisionMeta) return null;
                                      return (
                                        <span className={`badge badge-outline text-${reviewDecisionMeta.color}`}>
                                          {reviewDecisionMeta.label}
                                        </span>
                                      );
                                    })()}
                                    {urgencyBadge}
                                  </div>
                                  
                                  {/* Due date */}
                                  {dueLine && dueLine !== '-' && (
                                    <span className="small fw-medium" style={{ color: isCompleted ? '#15803d' : (category === 'overdue' ? '#b91c1c' : '#64748b') }}>
                                      {dueLine}
                                    </span>
                                  )}
                                </div>

                                {/* Card body: title + description */}
                                <div className="px-3 pt-2 pb-2">
                                  {/* Title */}
                                  <div className="fw-semibold mb-1" style={{ fontSize: '1.05rem', color: '#1e293b', textDecoration: isCompleted ? 'line-through' : 'none' }}>
                                    {title}
                                  </div>
                                  
                                  {/* Description */}
                                  {description && (
                                    <div className="text-muted small mb-2" style={{ lineHeight: '1.45' }}>
                                      {description}
                                    </div>
                                  )}
                                  
                                  {/* Company */}
                                  {company && (
                                    <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
                                      <span className="small text-muted fst-italic">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1" style={{ verticalAlign: 'middle' }}>
                                          <path d="M3 21h18" /><path d="M9 8h1" /><path d="M9 12h1" /><path d="M9 16h1" /><path d="M14 8h1" /><path d="M14 12h1" /><path d="M14 16h1" /><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
                                        </svg>
                                        {company}
                                      </span>
                                    </div>
                                  )}

                                  {/* Audit Methodology */}
                                  {auditMethodology && (
                                    <div className="small text-muted mb-1">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1" style={{ verticalAlign: 'middle' }}>
                                        <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 12l.01 0" /><path d="M13 12l2 0" /><path d="M9 16l.01 0" /><path d="M13 16l2 0" />
                                      </svg>
                                      Methodology: <span className="fw-medium" style={{ color: '#334155' }}>{auditMethodology}</span>
                                    </div>
                                  )}

                                  {/* Created date */}
                                  {(task?.createdAt || task?.created_at) && (
                                    <div className="small text-muted d-flex align-items-center gap-1">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                        <path d="M4 5a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1z" />
                                        <path d="M16 3v4" />
                                        <path d="M8 3v4" />
                                        <path d="M4 11h16" />
                                      </svg>
                                      Created {formatDateToDDMMYYYY(task?.createdAt || task?.created_at)}
                                    </div>
                                  )}
                                </div>

                                {/* Card footer: assignee + actions */}
                                <div
                                  className="px-3 py-2 d-flex align-items-center justify-content-between gap-2 flex-wrap"
                                  style={{ borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}
                                >
                                  {/* Assignee */}
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      className="avatar avatar-sm fw-bold"
                                      style={{
                                        background: isCompleted ? '#dcfce7' : taskTypeAccent.bg,
                                        color: isCompleted ? '#15803d' : taskTypeAccent.text,
                                        border: `1.5px solid ${isCompleted ? '#bbf7d0' : taskTypeAccent.color + '50'}`,
                                        fontSize: '0.72rem',
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {getInitials(assigneeName)}
                                    </span>
                                    <div>
                                      <div className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1 }}>Assigned to</div>
                                      <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#334155' }}>{assigneeName}</div>
                                    </div>
                                  </div>

                                  {/* Action buttons */}
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {/* Re-assign button — opens modal with competency-filtered users */}
                                    {!isCompleted && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={isReassigningThis}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setReassignTargetTask(task);
                                          setReassignSelectedUser(null);
                                          setReassignNotes('');
                                          setReassignSearch('');
                                          setAuditAssignees([]);
                                          setShowReassignModal(true);
                                          loadAuditAssignees(task?.taskTypeCode);
                                        }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                                          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                                        </svg>
                                        Re-assign
                                      </button>
                                    )}

                                    {/* View Audit Detail */}
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={() => navigate(task?.projectId || task?.id ? `/audit-process/${task.projectId || task.id}` : '/audit-process')}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                      </svg>
                                      View Detail
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                    {!isLoadingAuditTasks && !capabilitiesLoading && filteredAuditTasks.length > 0 && (
                      <div className="card-footer bg-transparent text-muted small d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>
                          Showing <strong className="text-body">
                            {(auditTaskPage - 1) * AUDIT_TASK_PAGE_SIZE + 1}-{Math.min(auditTaskPage * AUDIT_TASK_PAGE_SIZE, filteredAuditTasks.length)}
                          </strong> of <strong className="text-body">{filteredAuditTasks.length}</strong> open tasks
                        </span>
                        <div className="d-flex align-items-center gap-1" style={{ background: 'rgba(0, 0, 0, 0.03)', borderRadius: '24px', padding: '4px' }}>
                          <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            disabled={auditTaskPage <= 1}
                            aria-label="Previous page"
                            onClick={() => setAuditTaskPage((p) => Math.max(1, p - 1))}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              border: 'none',
                              padding: 0,
                              background: auditTaskPage <= 1 ? 'transparent' : '#fff',
                              color: auditTaskPage <= 1 ? '#c1c7d0' : '#1a767e',
                              boxShadow: auditTaskPage <= 1 ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
                              cursor: auditTaskPage <= 1 ? 'default' : 'pointer',
                            }}
                          >
                            ‹
                          </button>
                          <span className="px-2 fw-semibold" style={{ fontSize: '0.78rem', color: '#1a767e' }}>
                            {auditTaskPage} <span className="text-muted fw-normal">/ {auditTaskTotalPages}</span>
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            disabled={auditTaskPage >= auditTaskTotalPages}
                            aria-label="Next page"
                            onClick={() => setAuditTaskPage((p) => Math.min(auditTaskTotalPages, p + 1))}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              border: 'none',
                              padding: 0,
                              background: auditTaskPage >= auditTaskTotalPages ? 'transparent' : '#fff',
                              color: auditTaskPage >= auditTaskTotalPages ? '#c1c7d0' : '#1a767e',
                              boxShadow: auditTaskPage >= auditTaskTotalPages ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
                              cursor: auditTaskPage >= auditTaskTotalPages ? 'default' : 'pointer',
                            }}
                          >
                            ›
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Audit Task Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasAuditTask"
        title="Filter Audit Tasks"
        showPeriod={false}
        filterData={auditTaskFilterDraft}
        onChange={handleAuditTaskFilterChange}
        onApply={handleAuditTaskFilterApply}
        onReset={handleAuditTaskFilterReset}
        filterFields={auditTaskFilterFields}
      />

      {/* Task Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasTask"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={taskFilterFields}
      />

      {/* Assigned Leads Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasAssignedLeads"
        title="Filter Assigned Leads"
        showPeriod={false}
        filterData={assignedLeadsFilterData}
        onChange={handleAssignedLeadsFilterChange}
        onApply={handleAssignedLeadsFilterApply}
        onReset={handleAssignedLeadsFilterReset}
        filterFields={[
          { key: 'createdBy', label: 'Created By', type: 'select', options: filterOptions.assignedLeadsUsers },
          { key: 'assignedTo', label: 'Assigned To', type: 'select', options: filterOptions.assignedLeadsUsers },
          { key: 'stage', label: 'Stage', type: 'select', options: filterOptions.assignedLeadsStages },
        ]}
      />

      {/* Re-assign Modal */}
      {showReassignModal && reassignTargetTask && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }} onClick={() => setShowReassignModal(false)}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ border: 'none', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
              <div
                className="modal-header border-0"
                style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 55%, #2a8a93 100%)', padding: '1.1rem 1.4rem' }}
              >
                <h5 className="modal-title fw-semibold d-flex align-items-center gap-2 mb-0" style={{ color: '#fff' }}>
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34, borderRadius: '10px', background: 'rgba(255,255,255,0.16)' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                    </svg>
                  </span>
                  Re-assign Task
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowReassignModal(false)}
                />
              </div>

              <div className="modal-body" style={{ padding: '1.4rem' }}>
                <div
                  className="mb-3 p-3"
                  style={{ background: '#f8fafc', border: '1px solid #e6e9ef', borderRadius: '12px' }}
                >
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>
                    Task
                  </div>
                  <div className="fw-bold" style={{ color: '#1e293b' }}>
                    {reassignTargetTask?.title || reassignTargetTask?.taskType || '-'}
                  </div>
                  {(reassignTargetTask?.companyName || reassignTargetTask?.projectCode) && (
                    <div className="text-muted small mt-1 d-flex align-items-center gap-1 flex-wrap">
                      {reassignTargetTask?.companyName && <span className="fst-italic">{reassignTargetTask.companyName}</span>}
                      {reassignTargetTask?.projectCode && (
                        <span className="badge bg-secondary-lt text-secondary" style={{ fontSize: '0.7rem' }}>
                          {reassignTargetTask.projectCode}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label fw-semibold mb-0">
                      Assign To <span className="text-danger">*</span>
                    </label>
                    {isLoadingAuditAssignees && <span className="spinner-border spinner-border-sm text-secondary" role="status" />}
                  </div>

                  {reassignSelectedUser ? (
                    <div
                      className="d-flex align-items-center justify-content-between p-2 mb-2"
                      style={{ background: 'rgba(31,101,108,0.08)', border: '1px solid rgba(31,101,108,0.25)', borderRadius: '10px' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="avatar avatar-sm fw-bold"
                          style={{
                            background: 'rgba(31,101,108,0.16)',
                            color: '#1f656c',
                            border: '1.5px solid rgba(31,101,108,0.35)',
                            fontSize: '0.72rem',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(reassignSelectedUser.fullName)}
                        </span>
                        <div>
                          <div className="fw-semibold small" style={{ color: '#1f656c' }}>{reassignSelectedUser.fullName}</div>
                          {reassignSelectedUser.email && (
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>{reassignSelectedUser.email}</div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setReassignSelectedUser(null); setReassignSearch(''); }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="position-relative mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#8a94a6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                          <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                          <path d="M21 21l-6 -6" />
                        </svg>
                        <input
                          type="text"
                          className="form-control"
                          style={{ paddingLeft: '36px', borderRadius: '10px' }}
                          placeholder={isLoadingAuditAssignees ? 'Loading eligible users...' : 'Search eligible user by name or email...'}
                          disabled={isLoadingAuditAssignees}
                          value={reassignSearch}
                          onChange={(e) => setReassignSearch(e.target.value)}
                        />
                      </div>

                      {!isLoadingAuditAssignees && (
                        <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                          {filteredAuditAssignees.length} eligible user{filteredAuditAssignees.length !== 1 ? 's' : ''} for this task type
                        </div>
                      )}

                      <div className="border rounded overflow-auto" style={{ maxHeight: '220px', borderRadius: '10px', borderColor: '#e6e9ef' }}>
                        {isLoadingAuditAssignees ? (
                          <div className="p-3 text-center text-muted small">Loading eligible users...</div>
                        ) : filteredAuditAssignees.length === 0 ? (
                          <div className="p-3 text-center text-muted small">No eligible users found</div>
                        ) : (
                          filteredAuditAssignees.map((u) => (
                            <button
                              key={u?.id}
                              type="button"
                              className="d-flex align-items-center gap-2 w-100 text-start px-3 py-2 border-0"
                              style={{ background: '#fff', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                              onClick={() => { setReassignSelectedUser(u); setReassignSearch(''); }}
                            >
                              <span
                                className="avatar avatar-sm fw-bold flex-shrink-0"
                                style={{
                                  background: 'rgba(100,116,139,0.14)',
                                  color: '#475569',
                                  fontSize: '0.7rem',
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {getInitials(u?.fullName)}
                              </span>
                              <span className="d-flex flex-column overflow-hidden">
                                <span className="small fw-semibold text-truncate" style={{ color: '#1e293b' }}>{u?.fullName || '-'}</span>
                                {u?.email && <span className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>{u.email}</span>}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="mb-1">
                  <label className="form-label fw-semibold">Notes / Reason <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    style={{ borderRadius: '10px' }}
                    rows={3}
                    placeholder="Reason for re-assigning this task..."
                    value={reassignNotes}
                    onChange={(e) => setReassignNotes(e.target.value)}
                  />
                  {!reassignNotes.trim() && (
                    <div className="form-text text-danger">Notes is required before re-assigning.</div>
                  )}
                </div>
              </div>

              <div className="modal-footer border-0" style={{ padding: '1rem 1.4rem 1.4rem' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: '10px' }}
                  onClick={() => setShowReassignModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{
                    borderRadius: '10px',
                    background: (!reassignSelectedUser || !reassignNotes.trim() || !!reassigningAuditTaskId) ? '#94a3b8' : 'linear-gradient(135deg, #1f656c 0%, #2a8a93 100%)',
                    color: '#fff',
                    border: 'none',
                  }}
                  disabled={!reassignSelectedUser || !reassignNotes.trim() || !!reassigningAuditTaskId}
                  onClick={async () => {
                    setShowReassignModal(false);
                    await handleAuditReassign(reassignTargetTask?.id, reassignSelectedUser?.id, reassignNotes.trim());
                    setReassignTargetTask(null);
                    setReassignSelectedUser(null);
                    setReassignNotes('');
                    setReassignSearch('');
                  }}
                >
                  {reassigningAuditTaskId ? 'Re-assigning...' : 'Confirm Re-assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskManagement;
